// Consulta de Antecedentes (redesenho 2026-10-04) — transforma o JSON
// genérico da API Full (`dados` varia por base e nunca foi visto com token
// real) num perfil estruturado para os painéis da tela. Tudo é heurístico:
// procura chaves conhecidas em qualquer profundidade; o que não for
// reconhecido continua visível em "Dados completos".

export interface ProfileAddress {
  line: string;
  district?: string;
  city?: string;
  zip?: string;
  kind?: string;
  date?: string;
  source?: string;
}
export interface ProfilePhone {
  number: string;
  kind?: string;
  carrier?: string;
  source?: string;
}
export interface ProfileEmail {
  address: string;
  source?: string;
}
export interface ProfileVehicle {
  plate: string;
  model?: string;
  year?: string;
  color?: string;
  source?: string;
}
export interface ProfileProcess {
  number: string;
  court?: string;
  subject?: string;
  status?: string;
  date?: string;
}
export interface ProfileRecord {
  title: string;
  detail?: string;
  status: "nada_consta" | "consta" | "info";
  source?: string;
  /** Não é a certidão do PDF da consulta — sem link "Certidão PDF". */
  noPdf?: boolean;
}
export interface ProfileLink {
  name: string;
  relation?: string;
  document?: string;
}

export type ProfileList =
  | "addresses"
  | "phones"
  | "emails"
  | "vehicles"
  | "processes"
  | "records"
  | "links";

export interface Profile {
  name?: string;
  cpf?: string;
  rg?: string;
  birthDate?: string;
  nationality?: string;
  birthplace?: string;
  mother?: string;
  father?: string;
  status?: string;
  gender?: string;
  photo?: string;
  coords?: { lat: number; lng: number };
  addresses: ProfileAddress[];
  phones: ProfilePhone[];
  emails: ProfileEmail[];
  vehicles: ProfileVehicle[];
  processes: ProfileProcess[];
  records: ProfileRecord[];
  links: ProfileLink[];
  /** Outros dados cadastrais reconhecidos (PIS, Cartão SUS, renda…). */
  extras: { label: string; value: string }[];
  /** Listas que o tipo de consulta nem pesquisa — "não incluído", não "vazio". */
  notCovered?: Partial<Record<ProfileList, true>>;
}

export const emptyProfile = (): Profile => ({
  addresses: [],
  phones: [],
  emails: [],
  vehicles: [],
  processes: [],
  records: [],
  links: [],
  extras: [],
});

const norm = (k: string) =>
  k
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string | undefined => {
  if (v == null || v === "") return undefined;
  if (typeof v === "string") return v.trim() || undefined;
  if (typeof v === "number") return String(v);
  return undefined;
};

/** Primeiro valor (string/número) cuja chave normalizada casa com `re`. */
function pick(o: Obj, re: RegExp): string | undefined {
  for (const [k, v] of Object.entries(o))
    if (re.test(norm(k))) {
      const s = str(v);
      if (s) return s;
    }
  return undefined;
}

const SCALAR_FIELDS: [keyof Profile, RegExp][] = [
  ["name", /^(nome|nomecompleto|name|nomepessoa)$/],
  ["cpf", /^(cpf|numerocpf|nrcpf)$/],
  ["rg", /^(rg|identidade|numerorg|nrrg)$/],
  ["birthDate", /(nascimento|datanasc|dtnasc|datadenascimento)/],
  ["nationality", /nacionalidade/],
  ["birthplace", /(naturalidade|municipionascimento|localnascimento)/],
  ["mother", /^(mae|nomemae|nomedamae|filiacao1)$/],
  ["father", /^(pai|nomepai|nomedopai|filiacao2)$/],
  ["status", /^(situacao|situacaocadastral|situacaocpf|cpfsituacao|statusreceita|situacaorfb)$/],
  ["gender", /^(sexo|genero)$/],
];

// Ordem = ordem de exibição em "Informações pessoais".
const EXTRA_FIELDS: [string, RegExp][] = [
  ["Nome social", /^nomesocial$/],
  ["PIS / NIS", /^(numeropis|pis|nis|numeronis)$/],
  ["Cartão SUS", /^(cartaonacionalsaude|cns|numerocns)$/],
  ["Título de eleitor", /^(tituloeleitor|titulo)$/],
  ["CTPS", /^ctps$/],
  ["Estado civil", /^estadocivil$/],
  ["Escolaridade", /^(grauinstrucao|escolaridade)$/],
  ["Classe social", /^classesocial$/],
  ["Poder aquisitivo", /^poderaquisitivo$/],
  ["Faixa de renda", /^(faixarenda|rendapresumida|rendaestimada)$/],
  ["Dependentes", /^(numerodependentes|qtddependentes)$/],
  ["Indicação de óbito", /^(indicacaoobito|obito)$/],
];

function asAddress(o: Obj): ProfileAddress | null {
  const street = pick(o, /^(logradouro|endereco|rua|enderecocompleto)$/);
  if (!street) return null;
  const num = pick(o, /^(numero|nro|num)$/);
  const comp = pick(o, /^complemento$/);
  const city = pick(o, /^(cidade|municipio)$/);
  const uf = pick(o, /^(uf|estado)$/);
  const type = pick(o, /^(tipologradouro)$/);
  return {
    line:
      [type, street].filter(Boolean).join(" ") +
      (num ? `, ${num}` : "") +
      (comp ? ` – ${comp}` : ""),
    district: pick(o, /^bairro$/),
    city: [city, uf].filter(Boolean).join("/") || undefined,
    zip: pick(o, /^cep$/),
    kind: pick(o, /^(tipo|classificacao|tipoendereco)$/),
    date: pick(o, /(dataatualizacao|data|periodo|ultimaatualizacao)/),
    source: pick(o, /^(fonte|origem|base)$/),
  };
}
function asPhone(o: Obj): ProfilePhone | null {
  const n = pick(o, /^(telefone|fone|celular|numerotelefone|telefonecompleto)$/);
  const ddd = pick(o, /^ddd$/);
  const num = n ?? (ddd ? pick(o, /^numero$/) : undefined);
  if (!num) return null;
  return {
    number: ddd && !num.startsWith("(") ? `(${ddd}) ${num}` : num,
    kind: pick(o, /^(tipo|tipotelefone)$/),
    carrier: pick(o, /^(operadora)$/),
    source: pick(o, /^(fonte|origem|base)$/),
  };
}
function asVehicle(o: Obj): ProfileVehicle | null {
  const plate = pick(o, /^placa$/);
  if (!plate) return null;
  return {
    plate,
    model: pick(o, /^(modelo|marcamodelo|descricao)$/),
    year: pick(o, /^(ano|anomodelo|anofabricacao)$/),
    color: pick(o, /^cor$/),
    source: pick(o, /^(fonte|origem|base)$/),
  };
}
function asProcess(o: Obj): ProfileProcess | null {
  const n = pick(o, /^(numeroprocesso|processo|numero_processo|numerounico|nprocesso)$/);
  if (!n || !Object.keys(o).some((k) => /tribunal|vara|classe|assunto|processo/.test(norm(k))))
    return null;
  return {
    number: n,
    court: pick(o, /^(tribunal|vara|orgaojulgador|comarca)$/),
    subject: pick(o, /^(assunto|classe|classeprocessual)$/),
    status: pick(o, /^(situacao|status|fase)$/),
    date: pick(o, /(datadistribuicao|data|dataautuacao)/),
  };
}
function asLink(o: Obj): ProfileLink | null {
  const rel = pick(o, /^(parentesco|vinculo|relacionamento|tiporelacao|grau)$/);
  const name = pick(o, /^(nome|nomecompleto|nomevinculo|razaosocial)$/);
  if (!rel || !name) return null;
  return { name, relation: rel, document: pick(o, /^(cpf|cnpj|documento)$/) };
}

/** Constrói o perfil a partir do resultado bruto da API. */
export function buildProfile(raw: unknown, document?: string): Profile {
  const p = emptyProfile();
  const depthOf: Partial<Record<keyof Profile, number>> = {};
  const extraDepth = new Map<string, number>();
  const extras = new Map<string, string>();
  const seen = {
    addr: new Set<string>(),
    phone: new Set<string>(),
    email: new Set<string>(),
    plate: new Set<string>(),
    proc: new Set<string>(),
  };

  const walk = (v: unknown, key: string, depth: number) => {
    if (depth > 10) return;
    if (Array.isArray(v)) {
      const k = norm(key);
      v.forEach((item) => {
        if (typeof item === "string") {
          if (/email/.test(k) && item.includes("@") && !seen.email.has(item)) {
            seen.email.add(item);
            p.emails.push({ address: item });
          } else if (/(telefone|fone|celular)/.test(k) && !seen.phone.has(item)) {
            seen.phone.add(item);
            p.phones.push({ number: item });
          }
        }
        walk(item, key, depth + 1);
      });
      return;
    }
    if (!isObj(v)) return;

    for (const [field, re] of SCALAR_FIELDS) {
      const val = pick(v, re);
      if (val && (depthOf[field] == null || depth < depthOf[field]!)) {
        (p as unknown as Obj)[field] = val;
        depthOf[field] = depth;
      }
    }
    for (const [label, re] of EXTRA_FIELDS) {
      const val = pick(v, re);
      if (val && (!extraDepth.has(label) || depth < extraDepth.get(label)!)) {
        extras.set(label, val);
        extraDepth.set(label, depth);
      }
    }
    const photo = pick(v, /^(foto|fotobase64|imagem|photo)$/);
    if (
      photo &&
      !p.photo &&
      (photo.startsWith("data:image") || /^https?:\/\//.test(photo) || photo.length > 500)
    ) {
      p.photo =
        photo.startsWith("data:") || photo.startsWith("http")
          ? photo
          : `data:image/jpeg;base64,${photo}`;
    }
    const lat = Number(pick(v, /^(latitude|lat)$/));
    const lng = Number(pick(v, /^(longitude|lng|lon)$/));
    if (!p.coords && Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0)
      p.coords = { lat, lng };

    const a = asAddress(v);
    if (a && !seen.addr.has(a.line)) {
      seen.addr.add(a.line);
      p.addresses.push(a);
    }
    const ph = asPhone(v);
    if (ph && !seen.phone.has(ph.number)) {
      seen.phone.add(ph.number);
      p.phones.push(ph);
    }
    const em = pick(v, /^(email|enderecoemail|mail)$/);
    if (em && em.includes("@") && !seen.email.has(em)) {
      seen.email.add(em);
      p.emails.push({ address: em, source: pick(v, /^(fonte|origem|base)$/) });
    }
    const ve = asVehicle(v);
    if (ve && !seen.plate.has(ve.plate)) {
      seen.plate.add(ve.plate);
      p.vehicles.push(ve);
    }
    const pr = asProcess(v);
    if (pr && !seen.proc.has(pr.number)) {
      seen.proc.add(pr.number);
      p.processes.push(pr);
    }
    const li = asLink(v);
    if (li) p.links.push(li);

    // HEADER do CPF completo = só flags "0"/"1" de quais blocos vieram.
    for (const [k, child] of Object.entries(v))
      if (typeof child === "object" && !/^(header|dadosretornados)$/.test(norm(k)))
        walk(child, k, depth + 1);
  };
  walk(raw, "", 0);
  if (!p.cpf && document) p.cpf = document;
  p.extras = EXTRA_FIELDS.filter(([l]) => extras.has(l)).map(([label]) => ({
    label,
    value: extras.get(label)!,
  }));
  if (p.gender && /^[mf]$/i.test(p.gender))
    p.gender = /^m$/i.test(p.gender) ? "Masculino" : "Feminino";
  return p;
}

/** Certidão de antecedentes: tenta achar "nada consta"/"consta" no texto. */
export function certidaoRecord(raw: unknown, ok: boolean, message: string | null): ProfileRecord {
  const text = JSON.stringify(raw ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  if (!ok)
    return {
      title: "Certidão de antecedentes criminais (Polícia Federal)",
      detail: message ?? "A consulta não retornou dados.",
      status: "info",
      source: "GPS Drive API",
    };
  const nada = /nada consta|nao consta|nao constam|negativa/.test(text);
  const consta = !nada && /(consta|positiva|registro criminal)/.test(text);
  return {
    title: "Certidão de antecedentes criminais (Polícia Federal)",
    detail: nada
      ? "Não constam registros criminais na certidão emitida."
      : consta
        ? "A certidão indica registro. Abra o PDF/dados completos para conferir."
        : "Certidão emitida — confira os dados completos.",
    status: nada ? "nada_consta" : consta ? "consta" : "info",
    source: "GPS Drive API",
  };
}

// ---------------------------------------------------------------------------
// Demonstração — dados 100% FICTÍCIOS, só para visualizar o layout. Nenhum
// nome/documento abaixo pertence a uma pessoa real.
export const DEMO_PROFILE: Profile = {
  name: "CARLOS EDUARDO EXEMPLO DA SILVA",
  cpf: "12345678909",
  rg: "00.000.000-0 SSP/SP",
  birthDate: "15/03/1985",
  nationality: "Brasileira",
  birthplace: "São Paulo/SP",
  mother: "MARIA APARECIDA EXEMPLO",
  father: "JOSÉ ROBERTO DA SILVA",
  status: "Regular",
  gender: "Masculino",
  coords: { lat: -23.5613, lng: -46.6565 },
  addresses: [
    {
      line: "Avenida Paulista, 1000 – Apto 101",
      district: "Bela Vista",
      city: "São Paulo/SP",
      zip: "01310-100",
      kind: "Residencial",
      date: "Atual · 2024",
      source: "Base demonstrativa",
    },
    {
      line: "Rua Augusta, 500 – Sala 12",
      district: "Consolação",
      city: "São Paulo/SP",
      zip: "01305-000",
      kind: "Comercial",
      date: "2019 – 2023",
      source: "Base demonstrativa",
    },
    {
      line: "Rua das Flores, 45",
      district: "Centro",
      city: "Campinas/SP",
      zip: "13010-000",
      kind: "Residencial",
      date: "2012 – 2018",
      source: "Base demonstrativa",
    },
  ],
  phones: [
    {
      number: "(11) 90000-0001",
      kind: "Celular",
      carrier: "Operadora A",
      source: "Base demonstrativa",
    },
    {
      number: "(11) 3000-0002",
      kind: "Fixo",
      carrier: "Operadora B",
      source: "Base demonstrativa",
    },
  ],
  emails: [
    { address: "carlos.exemplo@example.com", source: "Base demonstrativa" },
    { address: "c.silva.demo@example.org", source: "Base demonstrativa" },
  ],
  vehicles: [
    {
      plate: "ABC1D23",
      model: "Hatch Exemplo 1.0",
      year: "2021",
      color: "Prata",
      source: "Base demonstrativa",
    },
    {
      plate: "XYZ9K87",
      model: "Sedan Exemplo 2.0",
      year: "2017",
      color: "Preto",
      source: "Base demonstrativa",
    },
  ],
  processes: [
    {
      number: "0000000-00.2022.8.26.0000",
      court: "TJSP – 1ª Vara Cível",
      subject: "Cobrança (exemplo)",
      status: "Arquivado",
      date: "10/05/2022",
    },
  ],
  records: [
    {
      title: "Certidão de antecedentes criminais (Polícia Federal)",
      detail: "Não constam registros criminais na certidão emitida.",
      status: "nada_consta",
      source: "Demonstração",
    },
  ],
  links: [
    { name: "MARIA APARECIDA EXEMPLO", relation: "Mãe" },
    { name: "ANA PAULA EXEMPLO", relation: "Cônjuge" },
    { name: "EMPRESA EXEMPLO LTDA", relation: "Sócio", document: "00.000.000/0001-00" },
  ],
  extras: [
    { label: "PIS / NIS", value: "000.00000.00-0" },
    { label: "Classe social", value: "B" },
  ],
};
